/**
 * chartjs-chart-wordcloud
 * https://github.com/sgratzl/chartjs-chart-wordcloud
 *
 * Copyright (c) 2019-2023 Samuel Gratzl <sam@sgratzl.com>
 */

import { Element, registry, DatasetController, Chart } from 'chart.js';
import { toFont } from 'chart.js/helpers';
import layout from 'd3-cloud';

class WordElement extends Element {
    static computeRotation(o, rnd) {
        if (o.rotationSteps <= 1) {
            return 0;
        }
        if (o.minRotation === o.maxRotation) {
            return o.minRotation;
        }
        const base = Math.min(o.rotationSteps, Math.floor(rnd() * o.rotationSteps)) / (o.rotationSteps - 1);
        const range = o.maxRotation - o.minRotation;
        return o.minRotation + base * range;
    }
    inRange(mouseX, mouseY) {
        const p = this.getProps(['x', 'y', 'width', 'height', 'scale']);
        if (p.scale <= 0) {
            return false;
        }
        const x = Number.isNaN(mouseX) ? p.x : mouseX;
        const y = Number.isNaN(mouseY) ? p.y : mouseY;
        return x >= p.x - p.width / 2 && x <= p.x + p.width / 2 && y >= p.y - p.height / 2 && y <= p.y + p.height / 2;
    }
    inXRange(mouseX) {
        return this.inRange(mouseX, Number.NaN);
    }
    inYRange(mouseY) {
        return this.inRange(Number.NaN, mouseY);
    }
    getCenterPoint() {
        return this.getProps(['x', 'y']);
    }
    tooltipPosition() {
        return this.getCenterPoint();
    }
    draw(ctx) {
        const { options } = this;
        const props = this.getProps(['x', 'y', 'width', 'height', 'text', 'scale']);
        if (props.scale <= 0) {
            return;
        }
        ctx.save();
        const f = toFont({ ...options, size: options.size * props.scale });
        ctx.font = f.string;
        ctx.fillStyle = options.color;
        ctx.textAlign = 'center';
        ctx.translate(props.x, props.y);
        ctx.rotate((options.rotate / 180) * Math.PI);
        if (options.strokeStyle) {
            if (options.strokeWidth != null) {
                ctx.lineWidth = options.strokeWidth;
            }
            ctx.strokeStyle = options.strokeStyle;
            ctx.strokeText(props.text, 0, 0);
        }
        ctx.fillText(props.text, 0, 0);
        ctx.restore();
    }
}
WordElement.id = 'word';
WordElement.defaults = {
    minRotation: -90,
    maxRotation: 0,
    rotationSteps: 2,
    padding: 1,
    strokeStyle: undefined,
    strokeWidth: undefined,
    size: (ctx) => {
        const v = ctx.parsed.y;
        return v;
    },
    hoverColor: '#ababab',
};
WordElement.defaultRoutes = {
    color: 'color',
    family: 'font.family',
    style: 'font.style',
    weight: 'font.weight',
    lineHeight: 'font.lineHeight',
};

function patchController(type, config, controller, elements = [], scales = []) {
    registry.addControllers(controller);
    if (Array.isArray(elements)) {
        registry.addElements(...elements);
    }
    else {
        registry.addElements(elements);
    }
    if (Array.isArray(scales)) {
        registry.addScales(...scales);
    }
    else {
        registry.addScales(scales);
    }
    const c = config;
    c.type = type;
    return c;
}

function rnd(seed = Date.now()) {
    let s = typeof seed === 'number' ? seed : Array.from(seed).reduce((acc, v) => acc + v.charCodeAt(0), 0);
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}
class WordCloudController extends DatasetController {
    constructor() {
        super(...arguments);
        this.wordLayout = layout()
            .text((d) => d.text)
            .padding((d) => d.options.padding)
            .rotate((d) => d.options.rotate)
            .font((d) => d.options.family)
            .fontSize((d) => d.options.size)
            .fontStyle((d) => d.options.style)
            .fontWeight((d) => { var _a; return (_a = d.options.weight) !== null && _a !== void 0 ? _a : 1; });
        this.rand = Math.random;
    }
    update(mode) {
        var _a;
        super.update(mode);
        const dsOptions = this.options;
        this.rand = rnd((_a = dsOptions.randomRotationSeed) !== null && _a !== void 0 ? _a : this.chart.id);
        const meta = this._cachedMeta;
        const elems = (meta.data || []);
        this.updateElements(elems, 0, elems.length, mode);
    }
    updateElements(elems, start, count, mode) {
        var _a, _b, _c, _d, _e;
        this.wordLayout.stop();
        const dsOptions = this.options;
        const xScale = this._cachedMeta.xScale;
        const yScale = this._cachedMeta.yScale;
        const w = xScale.right - xScale.left;
        const h = yScale.bottom - yScale.top;
        const labels = this.chart.data.labels;
        const growOptions = {
            maxTries: 3,
            scalingFactor: 1.2,
        };
        Object.assign(growOptions, (_a = dsOptions === null || dsOptions === void 0 ? void 0 : dsOptions.autoGrow) !== null && _a !== void 0 ? _a : {});
        const words = [];
        for (let i = start; i < start + count; i += 1) {
            const o = this.resolveDataElementOptions(i, mode);
            if (o.rotate == null) {
                o.rotate = WordElement.computeRotation(o, this.rand);
            }
            const properties = {
                options: { ...toFont(o), ...o },
                x: (_c = (_b = this._cachedMeta.xScale) === null || _b === void 0 ? void 0 : _b.getPixelForDecimal(0.5)) !== null && _c !== void 0 ? _c : 0,
                y: (_e = (_d = this._cachedMeta.yScale) === null || _d === void 0 ? void 0 : _d.getPixelForDecimal(0.5)) !== null && _e !== void 0 ? _e : 0,
                width: 10,
                height: 10,
                scale: 1,
                index: i,
                text: labels[i],
            };
            words.push(properties);
        }
        if (mode === 'reset') {
            words.forEach((tag) => {
                this.updateElement(elems[tag.index], tag.index, tag, mode);
            });
            return;
        }
        this.wordLayout.random(this.rand).words(words);
        const run = (factor = 1, tries = growOptions.maxTries) => {
            this.wordLayout
                .size([w * factor, h * factor])
                .on('end', (tags, bounds) => {
                if (tags.length < labels.length) {
                    if (tries > 0) {
                        const f = typeof growOptions.scalingFactor === 'function'
                            ? growOptions.scalingFactor(factor, tags, labels.length)
                            : factor * growOptions.scalingFactor;
                        run(f, tries - 1);
                        return;
                    }
                    console.warn(`cannot fit all text elements in ${growOptions.maxTries} tries`);
                }
                const wb = bounds[1].x - bounds[0].x;
                const hb = bounds[1].y - bounds[0].y;
                const scale = dsOptions.fit ? Math.min(w / wb, h / hb) : 1;
                const indices = new Set(labels.map((_, i) => i));
                tags.forEach((tag) => {
                    indices.delete(tag.index);
                    this.updateElement(elems[tag.index], tag.index, {
                        options: tag.options,
                        scale,
                        x: xScale.left + scale * tag.x + w / 2,
                        y: yScale.top + scale * tag.y + h / 2,
                        width: scale * tag.width,
                        height: scale * tag.height,
                        text: tag.text,
                    }, mode);
                });
                indices.forEach((i) => this.updateElement(elems[i], i, { scale: 0 }, mode));
            })
                .start();
        };
        run();
    }
    draw() {
        const elements = this._cachedMeta.data;
        const { ctx } = this.chart;
        elements.forEach((elem) => elem.draw(ctx));
    }
    getLabelAndValue(index) {
        const r = super.getLabelAndValue(index);
        const labels = this.chart.data.labels;
        r.label = labels[index];
        return r;
    }
}
WordCloudController.id = 'wordCloud';
WordCloudController.defaults = {
    datasets: {
        animation: {
            colors: {
                properties: ['color', 'strokeStyle'],
            },
            numbers: {
                properties: ['x', 'y', 'size', 'rotate'],
            },
        },
    },
    maintainAspectRatio: false,
    dataElementType: WordElement.id,
};
WordCloudController.overrides = {
    scales: {
        x: {
            type: 'linear',
            min: -1,
            max: 1,
            display: false,
        },
        y: {
            type: 'linear',
            min: -1,
            max: 1,
            display: false,
        },
    },
};
class WordCloudChart extends Chart {
    constructor(item, config) {
        super(item, patchController('wordCloud', config, WordCloudController, WordElement));
    }
}
WordCloudChart.id = WordCloudController.id;

export { WordCloudChart, WordCloudController, WordElement };
//# sourceMappingURL=index.js.map
